import { PhosphorFilterButtonWithLabel, PhosphorListButtonWithLabel } from "components/Atoms/PhosphorIconButtonWithLabel";
import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import VirtualizedFixedHeightList from "components/VirtualizedList/VirtualizedFixedHeightList";
import React from "react";
import { Message } from "semantic-ui-react";
import { DisplayMode } from "./PlanningMapApp";
import { usePrevious } from "utilities";
import { isAirportItem, isShowItem, isVenueItem, PlanningItem } from "./planningMapHelpers";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props<T> {
  uniqueId: string
  title: string;
  total: number;
  rowHeight: number;
  isLayerSelected: boolean;
  onLayerToggle: () => void;
  numberOfRows: number;
  displayMode: DisplayMode;
  onDisplayModeChange: (displayMode: DisplayMode) => void;
  onViewAllClick: () => void;
  startUncollapsed?: boolean;
  pin: React.ReactNode;
  hasFilters: boolean;
  isInitialLoading: boolean;
  error?: string;
  itemData: T;
  renderer: (props: { data: T, index: number, style: React.CSSProperties }) => JSX.Element;
  selectedItems: PlanningItem[];
  isFetching: boolean;
}

const PlanningMapListTitle = <T extends unknown>(props: React.PropsWithChildren<Props<T>>) => {

  const onViewAllClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.onViewAllClick();
  }

  if (!props.isLayerSelected || props.isFetching) {
    return <span style={{ marginTop: 1 }}>{props.title}</span>;
  }

  return <span style={{ marginTop: 1 }}>
    {props.title}: {props.numberOfRows}
    {props.total !== 0 && <>
      &nbsp;/&nbsp;
      <span onClick={onViewAllClick} className="text-light planning-map-view-all-link">
        {props.total}
      </span>
    </>}
  </span>
}

const PlanningMapList = <T extends unknown>(props: React.PropsWithChildren<Props<T>>) => {
  const virtualizedListRef = React.useRef(null);
  const previousSelectedItems = usePrevious(props.selectedItems);

  React.useEffect(() => {
    const getIds = (items: PlanningItem[]) => items.map(i => {
      if (isShowItem(i)) {
        return i.showId;
      } else if (isVenueItem(i)) {
        return i.venueId;
      } else if (isAirportItem(i)) {
        return i.airportId;
      }

      throw new Error("Unknown item type");
    });

    const previous = previousSelectedItems ?? [];
    const previousKeys = getIds(previous);
    const currentKeys = getIds(props.selectedItems);
    const hasChanged = (previous.length !== props.selectedItems.length) ||
      previousKeys.some((key, index) => key !== currentKeys[index]);

    if (hasChanged && props.selectedItems.length > 0) {
      virtualizedListRef.current?.scrollToItem(0);
    }
  }, [props.selectedItems])

  return <CollapsibleSection
    style="white"
    title={<div className="flex gap align-center">
      <Checkbox
        checked={props.isLayerSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          props.onLayerToggle();
        }}
      />
      {props.pin}
      <PlanningMapListTitle {...props} />
    </div>}
    uniqueRowId={props.uniqueId}
    disabled={!props.isLayerSelected}
    collapsible={props.startUncollapsed ? "startOpen" : "startClosed"}
  >
    <div className="flex gap margin-bottom">
      <PhosphorListButtonWithLabel
        selected={props.displayMode === "list"}
        onClick={() => props.onDisplayModeChange("list")}
      />
      <PhosphorFilterButtonWithLabel
        selected={props.displayMode === "filter"}
        onClick={() => props.onDisplayModeChange("filter")}
        dot={props.hasFilters}
      />
    </div>
    {(props.displayMode === "list" && props.numberOfRows > 0) && <div>
      <VirtualizedFixedHeightList
        height={400}
        width={280}
        rowHeight={props.rowHeight}
        itemCount={props.numberOfRows}
        itemData={props.itemData}
        renderer={props.renderer}
        ref={virtualizedListRef}
      />
    </div>}
    <div style={{ height: 400, display: (props.displayMode === "list" && props.numberOfRows === 0 && props.isInitialLoading) ? undefined : "none" }}>
      <Loading />
    </div>
    <div style={{ height: 400, display: (props.displayMode === "list" && props.numberOfRows === 0 && !props.isInitialLoading) ? undefined : "none" }}>
      {props.error ?
        <Message color="red">{props.error}</Message> :
        <NoDataAvailableDiv />}
    </div>
    <div style={{ display: props.displayMode === "filter" ? undefined : "none" }} className="vertical-filters flex-column gap-larger">
      {props.error && <Message color="red">{props.error}</Message>}
      {props.children}
    </div>
  </CollapsibleSection>
}

export default PlanningMapList;